import { api } from '../slices/api';
import { ManufacturingProcessType } from '../../interfaces/Manufacturing.Interface';
import {
	PaginatedProgramFeedbackResponse,
	PaginatedProgramIssueResponse,
	PaginatedProgramResponse,
	PaginatedProgramReviewResponse,
	PaginatedProgramScoringGridResponse,
	EnsureProgramPayload,
	Program,
	ProgramCreatePayload,
	ProgramFeedback,
	ProgramIssue,
	ProgramIssueCaughtPhase,
	ProgramIssueWritePayload,
	ProgramReview,
	ProgramScoringGridFilterUsers,
	ProgramScoresResponse,
	ProgramMetricsResponse,
	ProgramUploadedImage,
	KeyenceFilenameSuggestion,
	LinkableLineItemsResponse,
	ProgramWritePayload,
	ProgramType,
	SetProgramLineItemsPayload,
	CamOptimizationRow,
} from '../../interfaces/CamProgramming.interface';

const programReviewScoringInvalidations = [
	{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
	{ type: 'ProgramScores' as const, id: 'LIST' },
	{ type: 'ProgramMetrics' as const, id: 'LIST' },
];

export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getManufacturingProcessTypes: builder.query<
			ManufacturingProcessType[],
			void
		>({
			query: () => 'manufacturing/process-types',
		}),

		getPrograms: builder.query<
			PaginatedProgramResponse,
			{ [key: string]: any }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/programs/?${queryParams.toString()}`;
			},
			serializeQueryArgs: ({ queryArgs }) => {
				const normalized = Object.keys(queryArgs)
					.sort()
					.reduce<Record<string, unknown>>((acc, key) => {
						const value = queryArgs[key];
						if (value !== undefined && value !== null && value !== '') {
							acc[key] = value;
						}
						return acc;
					}, {});
				return JSON.stringify(normalized);
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: 'Program' as const,
								id,
							})),
							{ type: 'Program' as const, id: 'LIST' },
					  ]
					: [{ type: 'Program' as const, id: 'LIST' }],
		}),

		getProgramScoringGrid: builder.query<
			PaginatedProgramScoringGridResponse,
			{ [key: string]: unknown }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/programs/scoring-grid/?${queryParams.toString()}`;
			},
			serializeQueryArgs: ({ queryArgs }) => {
				const normalized = Object.keys(queryArgs)
					.sort()
					.reduce<Record<string, unknown>>((acc, key) => {
						const value = queryArgs[key];
						if (value !== undefined && value !== null && value !== '') {
							acc[key] = value;
						}
						return acc;
					}, {});
				return JSON.stringify(normalized);
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id, order_line_item }) => ({
								type: 'ProgramScoringGrid' as const,
								id: id ?? order_line_item.id,
							})),
							{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
							{ type: 'Program' as const, id: 'LIST' },
					  ]
					: [
							{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
							{ type: 'Program' as const, id: 'LIST' },
					],
		}),

		getProgramScoringGridFilterUsers: builder.query<
			ProgramScoringGridFilterUsers,
			{ program_type: string }
		>({
			query: ({ program_type }) =>
				`manufacturing/programs/scoring-grid/filter-users/?program_type=${program_type}`,
			providesTags: (_result, _error, { program_type }) => [
				{ type: 'ProgramScoringGrid' as const, id: `FILTER_USERS_${program_type}` },
			],
		}),

		getProgramScores: builder.query<
			ProgramScoresResponse,
			{ [key: string]: unknown }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/programs/scores/?${queryParams.toString()}`;
			},
			serializeQueryArgs: ({ queryArgs }) => {
				const normalized = Object.keys(queryArgs)
					.sort()
					.reduce<Record<string, unknown>>((acc, key) => {
						const value = queryArgs[key];
						if (value !== undefined && value !== null && value !== '') {
							acc[key] = value;
						}
						return acc;
					}, {});
				return JSON.stringify(normalized);
			},
			// Scores are derived from every program, review and issue in the
			// window, so any write to those entities can move them.
			providesTags: [
				{ type: 'ProgramScores' as const, id: 'LIST' },
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'ProgramReview' as const, id: 'LIST' },
			],
		}),

		getCamOptimizations: builder.query<CamOptimizationRow[], void>({
			query: () => 'manufacturing/cam-optimizations/',
			providesTags: [{ type: 'CamOptimizations' as const, id: 'LIST' }],
		}),

		setCamOptimized: builder.mutation<
			CamOptimizationRow,
			{ id: string; optimized: boolean }
		>({
			query: ({ id, optimized }) => ({
				url: `manufacturing/cam-optimizations/${id}/optimized/`,
				method: 'POST',
				body: { optimized },
			}),
			async onQueryStarted({ id, optimized }, { dispatch, queryFulfilled }) {
				const patch = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getCamOptimizations',
						undefined,
						(rows: CamOptimizationRow[]) => {
							const row = rows.find((r) => r.id === id);
							if (row) row.optimized = optimized;
						},
					),
				);
				try {
					const { data } = await queryFulfilled;
					dispatch(
						extendedApiSlice.util.updateQueryData(
							'getCamOptimizations',
							undefined,
							(rows: CamOptimizationRow[]) => {
								const index = rows.findIndex((r) => r.id === id);
								if (index >= 0) rows[index] = data;
							},
						),
					);
				} catch {
					patch.undo();
				}
			},
		}),

		getProgramMetrics: builder.query<
			ProgramMetricsResponse,
			{ [key: string]: unknown }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/programs/metrics/?${queryParams.toString()}`;
			},
			serializeQueryArgs: ({ queryArgs }) => {
				const normalized = Object.keys(queryArgs)
					.sort()
					.reduce<Record<string, unknown>>((acc, key) => {
						const value = queryArgs[key];
						if (value !== undefined && value !== null && value !== '') {
							acc[key] = value;
						}
						return acc;
					}, {});
				return JSON.stringify(normalized);
			},
			providesTags: [
				{ type: 'ProgramMetrics' as const, id: 'LIST' },
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'ProgramFeedback' as const, id: 'LIST' },
			],
		}),

		ensureProgram: builder.mutation<Program, EnsureProgramPayload>({
			query: (body) => ({
				url: 'manufacturing/programs/ensure/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [
				{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		getKeyenceFilenameSuggestions: builder.query<
			{ suggestions: KeyenceFilenameSuggestion[] },
			{ orderLineItemId: string; excludeProgram?: string | null }
		>({
			query: ({ orderLineItemId, excludeProgram }) => {
				const params = new URLSearchParams({
					order_line_item: orderLineItemId,
				});
				if (excludeProgram) {
					params.set('exclude_program', excludeProgram);
				}
				return `manufacturing/programs/keyence-filename-suggestions/?${params.toString()}`;
			},
		}),

		/**
		 * Other copies of the same part on this order that could share a
		 * program. Pass `program` to have the ones already linked come back
		 * flagged rather than missing.
		 */
		getLinkableLineItems: builder.query<
			LinkableLineItemsResponse,
			{ orderLineItemId: string; program?: string | null; programType: ProgramType }
		>({
			query: ({ orderLineItemId, program, programType }) => {
				const params = new URLSearchParams({
					order_line_item: orderLineItemId,
					program_type: programType,
				});
				if (program) {
					params.set('program', program);
				}
				return `manufacturing/programs/linkable-line-items/?${params.toString()}`;
			},
		}),

		setProgramLineItems: builder.mutation<Program, SetProgramLineItemsPayload>({
			query: ({ program, order_line_items, mode }) => ({
				url: `manufacturing/programs/${program}/line-items/`,
				method: 'POST',
				body: { order_line_items, mode: mode ?? 'set' },
			}),
			// Linking changes which line items render a placeholder row, so the
			// whole grid is stale, not just this program.
			invalidatesTags: (_result, _error, { program }) => [
				{ type: 'Program' as const, id: program },
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
			],
		}),

		getProgram: builder.query<Program, string>({
			query: (id) => `manufacturing/programs/${id}/`,
			providesTags: (_result, _error, id) => [{ type: 'Program' as const, id }],
		}),

		createProgram: builder.mutation<Program, ProgramCreatePayload>({
			query: (body) => ({
				url: 'manufacturing/programs/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
			],
		}),

		updateProgram: builder.mutation<
			Program,
			{ id: string; values: ProgramWritePayload }
		>({
			query: ({ id, values }) => ({
				url: `manufacturing/programs/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: 'Program' as const, id },
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
			],
		}),

		startProgram: builder.mutation<
			Program,
			{
				program?: string;
				filename?: string;
				order_name?: string;
				part_name?: string;
				program_type?: ProgramType;
			}
		>({
			query: (body) => ({
				url: 'manufacturing/programs/start/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (result) =>
				result
					? [
							{ type: 'Program' as const, id: result.id },
							{ type: 'Program' as const, id: 'LIST' },
							{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
					  ]
					: [
							{ type: 'Program' as const, id: 'LIST' },
							{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
					  ],
		}),

		completeProgram: builder.mutation<Program, { id: string }>({
			query: ({ id }) => ({
				url: `manufacturing/programs/${id}/complete/`,
				method: 'POST',
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: 'Program' as const, id },
				{ type: 'Program' as const, id: 'LIST' },
				{ type: 'ProgramScoringGrid' as const, id: 'LIST' },
			],
		}),

		uploadProgramImage: builder.mutation<
			ProgramUploadedImage,
			{ programId: string; file: File }
		>({
			query: ({ programId, file }) => {
				const formData = new FormData();
				formData.append('file', file);
				return {
					url: `manufacturing/programs/${programId}/upload-image/`,
					method: 'POST',
					body: formData,
				};
			},
		}),

		/**
		 * Stores an image the browser handed the editor as a URL rather than as
		 * bytes — pasting from Google Docs and friends gives HTML only, and
		 * cross-origin rules stop us reading the source ourselves.
		 */
		importProgramImage: builder.mutation<
			ProgramUploadedImage,
			{ programId: string; url: string }
		>({
			query: ({ programId, url }) => ({
				url: `manufacturing/programs/${programId}/import-image/`,
				method: 'POST',
				body: { url },
			}),
		}),

		getProgramIssues: builder.query<
			PaginatedProgramIssueResponse,
			{
				program: string;
				review?: string;
				caught_phase?: ProgramIssueCaughtPhase;
			}
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/program-issues/?${queryParams.toString()}`;
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: 'ProgramIssue' as const,
								id,
							})),
							{ type: 'ProgramIssue' as const, id: 'LIST' },
					  ]
					: [{ type: 'ProgramIssue' as const, id: 'LIST' }],
		}),

		createProgramIssue: builder.mutation<
			ProgramIssue,
			ProgramIssueWritePayload & { program: string }
		>({
			query: (body) => ({
				url: 'manufacturing/program-issues/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { program }) => [
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'Program' as const, id: program },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		updateProgramIssue: builder.mutation<
			ProgramIssue,
			{ id: string; programId: string; values: ProgramIssueWritePayload }
		>({
			query: ({ id, values }) => ({
				url: `manufacturing/program-issues/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramIssue' as const, id },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		deleteProgramIssue: builder.mutation<
			void,
			{ id: string; programId: string }
		>({
			query: ({ id }) => ({
				url: `manufacturing/program-issues/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramIssue' as const, id },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		getProgramReviews: builder.query<
			PaginatedProgramReviewResponse,
			{ program: string }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/program-reviews/?${queryParams.toString()}`;
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: 'ProgramReview' as const,
								id,
							})),
							{ type: 'ProgramReview' as const, id: 'LIST' },
					  ]
					: [{ type: 'ProgramReview' as const, id: 'LIST' }],
		}),

		getProgramReview: builder.query<ProgramReview, string>({
			query: (id) => `manufacturing/program-reviews/${id}/`,
			providesTags: (_result, _error, id) => [
				{ type: 'ProgramReview' as const, id },
			],
		}),

		createProgramReview: builder.mutation<
			ProgramReview,
			Partial<ProgramReview> & { program: string }
		>({
			query: (body) => ({
				url: 'manufacturing/program-reviews/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { program }) => [
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'Program' as const, id: program },
				{ type: 'Program' as const, id: 'LIST' },
				...programReviewScoringInvalidations,
			],
		}),

		// The server stamps both ends of a timed review, so neither endpoint
		// takes a time from the client.
		startProgramReview: builder.mutation<ProgramReview, { program: string }>({
			query: (body) => ({
				url: 'manufacturing/program-reviews/start/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { program }) => [
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'Program' as const, id: program },
				{ type: 'Program' as const, id: 'LIST' },
				...programReviewScoringInvalidations,
			],
		}),

		completeProgramReview: builder.mutation<
			ProgramReview,
			{ id: string; programId: string }
		>({
			query: ({ id }) => ({
				url: `manufacturing/program-reviews/${id}/complete/`,
				method: 'POST',
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramReview' as const, id },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
				...programReviewScoringInvalidations,
			],
		}),

		updateProgramReview: builder.mutation<
			ProgramReview,
			{ id: string; programId: string; values: Partial<ProgramReview> }
		>({
			query: ({ id, values }) => ({
				url: `manufacturing/program-reviews/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramReview' as const, id },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
				...programReviewScoringInvalidations,
			],
		}),

		deleteProgramReview: builder.mutation<
			void,
			{ id: string; programId: string }
		>({
			query: ({ id }) => ({
				url: `manufacturing/program-reviews/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramReview' as const, id },
				{ type: 'ProgramReview' as const, id: 'LIST' },
				{ type: 'ProgramIssue' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
				...programReviewScoringInvalidations,
			],
		}),

		getProgramFeedback: builder.query<
			PaginatedProgramFeedbackResponse,
			{ program: string }
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `manufacturing/program-feedback/?${queryParams.toString()}`;
			},
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: 'ProgramFeedback' as const,
								id,
							})),
							{ type: 'ProgramFeedback' as const, id: 'LIST' },
					  ]
					: [{ type: 'ProgramFeedback' as const, id: 'LIST' }],
		}),

		createProgramFeedback: builder.mutation<
			ProgramFeedback,
			Partial<ProgramFeedback> & { program: string }
		>({
			query: (body) => ({
				url: 'manufacturing/program-feedback/',
				method: 'POST',
				body,
			}),
			invalidatesTags: (_result, _error, { program }) => [
				{ type: 'ProgramFeedback' as const, id: 'LIST' },
				{ type: 'Program' as const, id: program },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		updateProgramFeedback: builder.mutation<
			ProgramFeedback,
			{ id: string; programId: string; values: Partial<ProgramFeedback> }
		>({
			query: ({ id, values }) => ({
				url: `manufacturing/program-feedback/${id}/`,
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramFeedback' as const, id },
				{ type: 'ProgramFeedback' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),

		deleteProgramFeedback: builder.mutation<
			void,
			{ id: string; programId: string }
		>({
			query: ({ id }) => ({
				url: `manufacturing/program-feedback/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, { id, programId }) => [
				{ type: 'ProgramFeedback' as const, id },
				{ type: 'ProgramFeedback' as const, id: 'LIST' },
				{ type: 'Program' as const, id: programId },
				{ type: 'Program' as const, id: 'LIST' },
			],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetManufacturingProcessTypesQuery,
	useGetProgramsQuery,
	useGetProgramScoringGridQuery,
	useGetProgramScoringGridFilterUsersQuery,
	useGetProgramScoresQuery,
	useGetProgramMetricsQuery,
	useGetCamOptimizationsQuery,
	useSetCamOptimizedMutation,
	useEnsureProgramMutation,
	useGetKeyenceFilenameSuggestionsQuery,
	useGetLinkableLineItemsQuery,
	useSetProgramLineItemsMutation,
	useGetProgramQuery,
	useCreateProgramMutation,
	useUpdateProgramMutation,
	useStartProgramMutation,
	useCompleteProgramMutation,
	useUploadProgramImageMutation,
	useImportProgramImageMutation,
	useGetProgramIssuesQuery,
	useCreateProgramIssueMutation,
	useUpdateProgramIssueMutation,
	useDeleteProgramIssueMutation,
	useGetProgramReviewsQuery,
	useGetProgramReviewQuery,
	useLazyGetProgramReviewQuery,
	useCreateProgramReviewMutation,
	useStartProgramReviewMutation,
	useCompleteProgramReviewMutation,
	useUpdateProgramReviewMutation,
	useDeleteProgramReviewMutation,
	useGetProgramFeedbackQuery,
	useCreateProgramFeedbackMutation,
	useUpdateProgramFeedbackMutation,
	useDeleteProgramFeedbackMutation,
} = extendedApiSlice;
